import type { AerosyncEnvironment } from 'aerosync-web-sdk';

import { getRuntimeConfig } from '@jane/shared/runtime-config';

export interface Config {
  actionCablePath: string;
  aeropayConsumerId: string;
  aeropayEnv: AerosyncEnvironment.Sandbox | AerosyncEnvironment.Production;
  algoliaApiKey: string;
  algoliaAppId: string;
  algoliaEnv: string;
  algoliaUrl: string;
  allowSkipTermsGate?: boolean;
  apiPath: string;
  apiPathBase: string;
  apiPathV2: string;
  appVersion: string;
  appleClientId: string;
  branchIoKey: string;
  brandLaunchDarklyClientID: string;
  brandMixpanelToken: string;
  brandPortalUrl: string;
  brandServerUrl: string;
  brandsDatadogAppId: string;
  brandsDatadogClientToken: string;
  brazeAPIKey: string;
  buildVersion: string;
  businessDatadogAppId: string;
  businessDatadogClientToken: string;
  businessLaunchDarklyClientID: string;
  businessMixpanelCustomerToken: string;
  businessUrl: string;
  canPayV2RemotePaySDK: string;
  cartLimitDuplicateRuleUserIds: number[];
  datadogEnv?: string;
  deployPublicPath: string;
  deployPublicPathWithoutUriScheme: string;
  dev: boolean;
  disableRecaptcha: boolean;
  dmEndpoint: string;
  dmMixpanelToken: string;
  dmSdkApiKey: string;
  dmServiceToken: string;
  facebookAppId: string;
  features: string[];
  fingerprintApiKey: string;
  fingerprintEndpoint: string;
  fingerprintScriptPath: string;
  fingerprintTimeoutMs?: number;
  framelessEmbedApiUrl: string;
  framelessEmbedMode: boolean;
  googleAnalyticsBusinessTrackingId: string;
  googleAnalyticsCustomerTrackingId: string;
  googleClientId: string;
  googleFontsKey: string;
  googlePlacesKey: string;
  idAnalyticsUrl?: string;
  janeSupportManagerIds: number[];
  kioskMixpanelCustomerToken: string;
  kioskV2Url: string;
  launchDarklyClientID: string;
  logMixpanelToConsole?: boolean;
  mapboxAccessToken: string;
  mfaForceSMS?: boolean;
  mixpanelCrmToken: string;
  mixpanelCustomerToken: string;
  mixpanelDomain: string;
  monerisEnv: 'qa' | 'prod';
  monerisJsSdkUrl: string;
  recaptchaKey: string;
  retailTimeout?: number;
  retailUrl?: string;
  rootsOnlyCustomRowStoreIds: number[];
  roundingBetaStoreIds: number[];
  ruleBasedCustomRowsStoreIds: number[];
  showAbandonedCartConfig?: boolean;
  showBrowserUpdate?: boolean;
  squareApplicationId: string;
  squareWebPaymentsSdkUrl: string;
  streamChatApiToken: string;
  strongholdEnv: 'sandbox' | 'live';
  strongholdIntegrationId: string;
  trackJsApplication: string;
  trackJsDomain: string;
  trackJsToken: string;
  undoableWindowMs?: number;
  useEmailOverSMS?: boolean;
}

function addBaseUrl(relativePath: string, deployPublicPath?: string | null) {
  let baseUrl = deployPublicPath ?? '';
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
  return `${baseUrl}${relativePath}`;
}

function addFramelessEmbedUrl(
  relativePath: string,
  framelessEmbedApiUrl?: string | null
) {
  let baseUrl = framelessEmbedApiUrl ?? '';
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);

  let relativeNoApiPath = relativePath;
  if (relativeNoApiPath.startsWith('/api'))
    relativeNoApiPath = relativeNoApiPath.slice(4);

  return `${baseUrl}${relativeNoApiPath}`;
}

function getApiPath(
  relativePath = '',
  deployPublicPath?: string | null,
  framelessEmbedApiUrl?: string | null
) {
  const { partnerHostedStoreId, isFramelessEmbedMode } = getRuntimeConfig();
  const needsBaseUrl = partnerHostedStoreId && !isFramelessEmbedMode;

  if (isFramelessEmbedMode)
    return addFramelessEmbedUrl(relativePath, framelessEmbedApiUrl);

  return needsBaseUrl
    ? addBaseUrl(relativePath, deployPublicPath)
    : relativePath;
}

function getJsonFromScriptTag(id: string) {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`${id} element not found`);
  }
  if (!el.textContent) {
    throw new Error(`${id} element is empty`);
  }
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    throw new Error(`Failed to parse JSON from ${id}: ${error}`);
  }
}

function getAppVersion(): string {
  if (typeof __JANE_APP_VERSION__ !== 'undefined' && __JANE_APP_VERSION__) {
    return __JANE_APP_VERSION__;
  }

  return 'dev';
}

function getBuildVersion(): string {
  if (typeof __JANE_BUILD_VERSION__ !== 'undefined' && __JANE_BUILD_VERSION__) {
    return __JANE_BUILD_VERSION__;
  }

  return 'dev';
}

const settings = getJsonFromScriptTag('jane-app-settings');
const secrets = getJsonFromScriptTag('jane-app-secrets');

export const config: Config = {
  actionCablePath: settings.actionCablePath,
  aeropayConsumerId: settings.aeropayConsumerId,
  aeropayEnv: settings.aeropayEnv,
  algoliaApiKey: secrets.algoliaApiKey,
  algoliaAppId: settings.algoliaAppId,
  algoliaEnv: settings.algoliaEnv,
  algoliaUrl: settings.algoliaUrl,
  allowSkipTermsGate: settings.allowSkipTermsGate,
  apiPath: getApiPath(
    settings.apiPath,
    settings.deployPublicPath,
    settings.framelessEmbedApiUrl
  ),
  apiPathBase: getApiPath(
    settings.apiPathBase,
    settings.deployPublicPath,
    settings.framelessEmbedApiUrl
  ),
  apiPathV2: getApiPath(
    settings.apiPathV2,
    settings.deployPublicPath,
    settings.framelessEmbedApiUrl
  ),
  appVersion: getAppVersion(),
  appleClientId: settings.appleClientId,
  branchIoKey: secrets.branchIoKey,
  brandLaunchDarklyClientID: settings.brandLaunchDarklyClientID,
  brandMixpanelToken: secrets.brandMixpanelToken,
  brandPortalUrl: settings.brandPortalUrl,
  brandServerUrl: settings.brandServerUrl,
  brandsDatadogAppId: settings.brandsDatadogAppId,
  brandsDatadogClientToken: secrets.brandsDatadogClientToken,
  brazeAPIKey: secrets.brazeAPIKey,
  buildVersion: getBuildVersion(),
  businessLaunchDarklyClientID: settings.businessLaunchDarklyClientID,
  businessDatadogAppId: settings.businessDatadogAppId,
  businessDatadogClientToken: secrets.businessDatadogClientToken,
  businessMixpanelCustomerToken: secrets.businessMixpanelCustomerToken,
  businessUrl: settings.businessUrl,
  canPayV2RemotePaySDK: settings.canPayV2RemotePaySDK,
  cartLimitDuplicateRuleUserIds: settings.cartLimitDuplicateRuleUserIds || [],
  deployPublicPath: settings.deployPublicPath,
  deployPublicPathWithoutUriScheme: settings.deployPublicPathWithoutUriScheme,
  datadogEnv: settings.datadogEnv,
  dev: settings.dev,
  disableRecaptcha: Boolean(settings.disableRecaptcha),
  dmEndpoint: settings.dmEndpoint,
  dmMixpanelToken: secrets.dmMixpanelToken,
  dmSdkApiKey: secrets.dmSdkApiKey,
  dmServiceToken: secrets.dmServiceToken,
  facebookAppId: settings.facebookAppId,
  features: settings.features,
  fingerprintApiKey: secrets.fingerprintApiKey,
  fingerprintEndpoint: settings.fingerprintEndpoint,
  fingerprintScriptPath: settings.fingerprintScriptPath,
  fingerprintTimeoutMs: settings.fingerprintTimeoutMs,
  framelessEmbedApiUrl: settings.framelessEmbedApiUrl,
  framelessEmbedMode: getRuntimeConfig().isFramelessEmbedMode,
  googleAnalyticsBusinessTrackingId: settings.googleAnalyticsBusinessTrackingId,
  googleAnalyticsCustomerTrackingId: settings.googleAnalyticsCustomerTrackingId,
  googleClientId: settings.googleClientId,
  googleFontsKey: secrets.googleFontsKey,
  googlePlacesKey: secrets.googlePlacesKey,
  idAnalyticsUrl: settings.idAnalyticsUrl,
  janeSupportManagerIds: settings.janeSupportManagerIds,
  kioskMixpanelCustomerToken: secrets.kioskMixpanelCustomerToken,
  kioskV2Url: settings.kioskV2Url,
  launchDarklyClientID: settings.launchDarklyClientID,
  logMixpanelToConsole: settings.logMixpanelToConsole,
  mapboxAccessToken: secrets.mapboxAccessToken,
  mfaForceSMS: settings.mfaForceSMS,
  mixpanelCrmToken: secrets.mixpanelCrmToken,
  mixpanelCustomerToken: secrets.mixpanelCustomerToken,
  mixpanelDomain: settings.mixpanelDomain,
  monerisEnv: settings.monerisEnv,
  monerisJsSdkUrl: settings.monerisJsSdkUrl,
  recaptchaKey: secrets.recaptchaKey,
  retailTimeout: settings.retailTimeout,
  retailUrl: settings.retailUrl,
  rootsOnlyCustomRowStoreIds: settings.rootsOnlyCustomRowStoreIds,
  roundingBetaStoreIds: settings.roundingBetaStoreIds,
  ruleBasedCustomRowsStoreIds: settings.ruleBasedCustomRowsStoreIds,
  showAbandonedCartConfig: settings.showAbandonedCartConfig,
  showBrowserUpdate: settings.showBrowserUpdate,
  squareApplicationId: settings.squareApplicationId,
  squareWebPaymentsSdkUrl: settings.squareWebPaymentsSdkUrl,
  streamChatApiToken: secrets.streamChatApiToken,
  strongholdEnv: settings.strongholdEnv,
  strongholdIntegrationId: settings.strongholdIntegrationId,
  trackJsApplication: settings.trackJsApplication,
  trackJsDomain: settings.trackJsDomain,
  trackJsToken: secrets.trackJsToken,
  undoableWindowMs: settings.undoableWindowMs,
  useEmailOverSMS: settings.useEmailOverSMS,
};

enum Feature {
  AeropayWhiteLabel = 'aeropayWhiteLabel',
  AppleSignIn = 'appleSignIn',
  CurbsidePickupNotify = 'curbsidePickupNotify',
  CustomRows = 'customRows',
  GoogleSignIn = 'googleSignIn',
  GuestCheckout = 'guestCheckout',
  OwnershipIdentification = 'ownershipIdentification',
  RootsOnlyCustomRows = 'rootsOnlyCustomRows',
  RoundingDisplay = 'roundingDisplay',
  Square = 'square',
  Stronghold = 'stronghold',
  StrongholdBusiness = 'strongholdBusiness',
  TwoWayMessaging = 'twoWayMessaging',
  VariableMaxOrders = 'variableMaxOrders',
}

type FeatureKey = `${Feature}`;

export const isFeatureEnabled = (feature: FeatureKey) => {
  return config.features.includes(feature);
};
